import React from 'react';
import { 
  X,
  Settings,
  Theater,
  Upload,
  Hash,
  CheckCircle,
  FileSpreadsheet,
  Users,
  Edit,
  Trash2,
  Loader2,
  AlertCircle,
  ChevronRight,
  Calendar,
  MapPin,
  Plus
} from 'lucide-react';

const StageManagementModal = ({
  isOpen,
  onClose,
  selectedEvent,
  isDarkMode,
  currentStep,
  setCurrentStep,
  stageCount,
  setStageCount,
  stageNames,
  setStageNames,
  csvFile,
  setCsvFile,
  csvError,
  setCsvError,
  parsedRoutines,
  setParsedRoutines,
  uploadingStageId,
  eventStages,
  selectedStage,
  modalNotification,
  setModalNotification,
  onConfigureStages,
  onStageCountChange,
  onStageNameChange,
  onFileSelect,
  onViewSchedule,
  onStageUpload,
  onSaveSchedule,
  onRemoveSchedule
}) => {
  if (!isOpen || !selectedEvent) return null;

  const steps = [
    { id: 0, title: 'Setup', description: 'Configure stages' },
    { id: 1, title: 'Manage', description: 'Upload schedules' }
  ];

  const currentStepData = steps[currentStep];

  // Handle direct file upload for stages
  const handleDirectUpload = (stage) => {
    console.log('Upload button clicked for stage:', stage);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.multiple = false;
    
    input.onchange = (event) => {
      console.log('File selected:', event.target.files[0]);
      const file = event.target.files[0];
      if (file) {
        // Check file type - allow both text/csv and application/vnd.ms-excel
        const isCSV = file.type === 'text/csv' || 
                     file.type === 'application/vnd.ms-excel' || 
                     file.name.toLowerCase().endsWith('.csv');
        
        if (isCSV) {
          console.log('Valid CSV file, calling onFileSelect');
          onFileSelect(event, stage);
        } else {
          console.error('Invalid file type:', file.type, file.name);
          alert(`Please select a valid CSV file. Selected file type: ${file.type}`);
        }
      } else {
        console.log('No file selected');
      }
      
      // Clean up the input element
      input.remove();
    };
    
    input.onerror = (error) => {
      console.error('File input error:', error);
    };
    
    input.click();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div 
          className={`relative w-full max-w-5xl rounded-xl shadow-2xl transform transition-all ${
            isDarkMode 
              ? 'bg-slate-900 border border-slate-700' 
              : 'bg-white border border-gray-100'
          }`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header with Progress */}
          <div className={`px-8 py-6 border-b ${
            isDarkMode ? 'border-slate-700' : 'border-gray-100'
          }`}>
            {/* Event Info */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-lg ${
                  isDarkMode ? 'bg-slate-800' : 'bg-gray-50'
                }`}>
                  <Theater className={`h-6 w-6 ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-600'
                  }`} />
                </div>
                <div>
                  <h2 className={`text-2xl font-bold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Stage Management
                  </h2>
                  <div className="flex items-center space-x-4 mt-1">
                    <div className="flex items-center space-x-1">
                      <Calendar className={`h-4 w-4 ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-500'
                      }`} />
                      <span className={`text-sm font-medium ${
                        isDarkMode ? 'text-slate-300' : 'text-gray-700'
                      }`}>
                        {selectedEvent.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className={`h-4 w-4 ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-500'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-500'
                      }`}>
                        {selectedEvent.city}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <button 
                onClick={onClose} 
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-300' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

                         {/* Progress Steps */}
             <div className="flex items-center justify-center space-x-4">
               {steps.map((step, index) => (
                 <div key={step.id} className="flex items-center">
                   <div className="flex items-center space-x-3">
                     <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                       currentStep >= step.id
                         ? isDarkMode 
                           ? 'bg-blue-600 border-blue-600 text-white' 
                           : 'bg-blue-600 border-blue-600 text-white'
                         : isDarkMode
                           ? 'border-slate-600 text-slate-400'
                           : 'border-gray-300 text-gray-400'
                     }`}>
                       {currentStep > step.id ? (
                         <CheckCircle className="h-5 w-5" />
                       ) : (
                         <span className="text-sm font-semibold">{step.id + 1}</span>
                       )}
                     </div>
                     <div className="hidden sm:block">
                       <p className={`text-sm font-semibold ${
                         currentStep >= step.id
                           ? isDarkMode ? 'text-white' : 'text-gray-900'
                           : isDarkMode ? 'text-slate-400' : 'text-gray-500'
                       }`}>
                         {step.title}
                       </p>
                       <p className={`text-xs ${
                         isDarkMode ? 'text-slate-500' : 'text-gray-400'
                       }`}>
                         {step.description}
                       </p>
                     </div>
                   </div>
                   {index < steps.length - 1 && (
                     <ChevronRight className={`h-5 w-5 mx-4 ${
                       isDarkMode ? 'text-slate-600' : 'text-gray-300'
                     }`} />
                   )}
                 </div>
               ))}
             </div>
          </div>

          {/* Modal Notification */}
          {modalNotification && (
            <div className={`mx-8 mt-4 p-4 rounded-lg flex items-center space-x-3 ${
              modalNotification.type === 'error' 
                ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800' 
                : 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800'
            }`}>
              {modalNotification.type === 'error' ? (
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
              ) : (
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
              )}
              <span className="flex-1">{modalNotification.message}</span>
              <button 
                onClick={() => setModalNotification(null)}
                className="text-current hover:opacity-70 transition-opacity"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Content */}
          <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
            
            {/* Step 1: Configure Stages */}
            {currentStep === 0 && (
                             <div className="space-y-6">
                 <div className="text-center">
                   <h3 className={`text-xl font-bold mb-2 ${
                     isDarkMode ? 'text-white' : 'text-gray-900'
                   }`}>
                     Set up your event stages
                   </h3>
                   <p className={`text-sm max-w-md mx-auto ${
                     isDarkMode ? 'text-slate-400' : 'text-gray-600'
                   }`}>
                     Configure the number of stages for your event and give them descriptive names
                   </p>
                 </div>

                <div className="max-w-2xl mx-auto">
                  <div className={`p-6 rounded-lg border ${
                    isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-semibold mb-4 ${
                      isDarkMode ? 'text-slate-200' : 'text-gray-800'
                    }`}>
                      Stage names
                    </label>
                    <div className="space-y-3">
                      {stageNames.map((name, index) => (
                        <div key={index} className="flex items-center space-x-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                            isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-white border-2 border-gray-200 text-gray-700'
                          }`}>
                            {index + 1}
                          </div>
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => onStageNameChange(index, e.target.value)}
                            placeholder={`e.g., Main Stage, Side Stage, Dance Floor`}
                            className={`flex-1 px-4 py-3 rounded-lg border ${
                              isDarkMode 
                                ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                          />
                          {stageNames.length > 1 && (
                            <button
                              onClick={() => {
                                const newStageNames = stageNames.filter((_, i) => i !== index);
                                setStageNames(newStageNames);
                                setStageCount(newStageNames.length);
                              }}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode 
                                  ? 'text-slate-400 hover:text-red-400 hover:bg-red-900/20' 
                                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                              }`}
                              title="Remove stage"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      
                      {/* Add Stage Button */}
                      {stageNames.length < 10 && (
                        <button
                          onClick={() => {
                            const newStageNames = [...stageNames, `Stage ${stageNames.length + 1}`];
                            setStageNames(newStageNames);
                            setStageCount(newStageNames.length);
                          }}
                          className={`w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed rounded-lg transition-all ${
                            isDarkMode 
                              ? 'border-slate-600 text-slate-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-900/10' 
                              : 'border-gray-300 text-gray-500 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          <Plus className="h-5 w-5" />
                          <span>Add another stage</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                                 <div className="flex justify-end gap-3 pt-6">
                   <button
                     onClick={onClose}
                     className={`px-6 py-2 rounded-lg border font-medium transition-colors ${
                       isDarkMode 
                         ? 'border-slate-600 text-slate-300 hover:bg-slate-800' 
                         : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                     }`}
                   >
                     Cancel
                   </button>
                   <button
                     onClick={onConfigureStages}
                     className="px-8 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
                   >
                     Continue
                     <ChevronRight className="h-4 w-4 ml-2" />
                   </button>
                 </div>
              </div>
            )}

            {/* Step 2: Manage Stages */}
            {currentStep === 1 && eventStages[selectedEvent.name] && (
                             <div className="space-y-6">
                 <div className="text-center">
                   <h3 className={`text-xl font-bold mb-2 ${
                     isDarkMode ? 'text-white' : 'text-gray-900'
                   }`}>
                     Manage your stages
                   </h3>
                   <p className={`text-sm max-w-md mx-auto ${
                     isDarkMode ? 'text-slate-400' : 'text-gray-600'
                   }`}>
                     Upload schedules and manage content for each stage
                   </p>
                 </div>

                                 <div className="space-y-4">
                   {eventStages[selectedEvent.name].stages.map((stage) => (
                     <div key={stage.id} className={`p-6 rounded-xl border transition-all hover:shadow-lg ${
                       isDarkMode 
                         ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-800' 
                         : 'bg-white border-gray-200 hover:shadow-xl hover:border-gray-300'
                     }`}>
                       <div className="flex items-center justify-between">
                         {/* Left: Stage Info */}
                         <div className="flex items-center space-x-4">
                           <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                             stage.schedule 
                               ? isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                               : isDarkMode ? 'bg-slate-700' : 'bg-gray-100 text-gray-500'
                           }`}>
                             <Hash className="h-6 w-6" />
                           </div>
                           
                           <div className="flex-1">
                             <div className="flex items-center space-x-3 mb-2">
                               <h4 className={`font-bold text-lg ${
                                 isDarkMode ? 'text-white' : 'text-gray-900'
                               }`}>
                                 {stage.name}
                               </h4>
                               <span className={`text-sm px-2 py-1 rounded-full ${
                                 isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                               }`}>
                                 Stage {stage.id}
                               </span>
                             </div>
                             
                            {/* Status */}
                            {(stage.schedule || (stage.hasEnhancedData && stage.routineCount > 0)) ? (
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className={`h-4 w-4 ${
                                    isDarkMode ? 'text-green-400' : 'text-green-600'
                                  }`} />
                                  <span className={`font-medium text-sm ${
                                    isDarkMode ? 'text-green-400' : 'text-green-700'
                                  }`}>
                                    {stage.hasEnhancedData ? 'Enhanced Schedule Active' : 'Schedule Active'}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Users className={`h-4 w-4 ${
                                    isDarkMode ? 'text-slate-400' : 'text-gray-500'
                                  }`} />
                                  <span className={`text-sm ${
                                    isDarkMode ? 'text-slate-300' : 'text-gray-700'
                                  }`}>
                                    {stage.routineCount || (stage.schedule ? stage.schedule.length : 0)} routines
                                  </span>
                                </div>
                              </div>
                             ) : (
                               <div className="flex items-center space-x-2">
                                 <FileSpreadsheet className={`h-4 w-4 ${
                                   isDarkMode ? 'text-amber-400' : 'text-amber-600'
                                 }`} />
                                 <span className={`font-medium text-sm ${
                                   isDarkMode ? 'text-amber-400' : 'text-amber-700'
                                 }`}>
                                   No Schedule
                                 </span>
                                 <span className={`text-xs ${
                                   isDarkMode ? 'text-slate-400' : 'text-gray-500'
                                 }`}>
                                   • Upload CSV to get started
                                 </span>
                               </div>
                             )}
                           </div>
                         </div>

                        {/* Right: Actions */}
                        <div className="flex items-center space-x-3">
                          {(stage.schedule || (stage.hasEnhancedData && stage.routineCount > 0)) && (
                            <button
                              onClick={() => onRemoveSchedule(stage)}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode 
                                  ? 'text-slate-400 hover:text-red-400 hover:bg-red-900/20' 
                                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                              }`}
                              title="Remove schedule"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          
                          {(stage.schedule || (stage.hasEnhancedData && stage.routineCount > 0)) && (
                            <button
                              onClick={() => onViewSchedule(stage)}
                              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center mr-3 ${
                                isDarkMode 
                                  ? 'bg-green-700 text-green-200 hover:bg-green-600' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              <FileSpreadsheet className="h-4 w-4 mr-2" />
                              View
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDirectUpload(stage)}
                            className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center ${
                              (stage.schedule || (stage.hasEnhancedData && stage.routineCount > 0))
                                ? isDarkMode 
                                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            {(stage.schedule || (stage.hasEnhancedData && stage.routineCount > 0)) ? 'Update' : 'Upload'}
                          </button>
                        </div>
                       </div>
                     </div>
                   ))}
                </div>

                                 <div className="flex justify-end gap-3 pt-6">
                   <button
                     onClick={() => setCurrentStep(0)}
                     className={`px-6 py-2 rounded-lg border font-medium transition-colors ${
                       isDarkMode 
                         ? 'border-slate-600 text-slate-300 hover:bg-slate-800' 
                         : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                     }`}
                   >
                     Back
                   </button>
                   <button
                     onClick={onClose}
                     className="px-8 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                   >
                     Save
                   </button>
                 </div>
              </div>
            )}

            
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageManagementModal;